// src/components/user/Masivo/FileUploader.jsx
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Upload, 
  FileText, 
  X, 
  Download, 
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { downloadExcelTemplate } from '../../../utils/excelParser';

function FileUploader({ onFileSelect, selectedFile, onClearFile, disabled, userProducts = [] }) {
  const [dragError, setDragError] = useState(null);

  function onDrop(acceptedFiles, rejectedFiles) {
    setDragError(null);
    
    if (rejectedFiles.length > 0) {
      setDragError('Solo se aceptan archivos Excel (.xlsx, .xls)');
      return;
    }
    
    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles[0]);
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled: disabled
  });

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 sm:p-8">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-primary to-[#E6B43C] flex items-center justify-center flex-shrink-0">
          <Upload className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-extrabold text-brand-primary font-montserrat mb-2">
            Paso 2: Sube tu Excel
          </h2>
          <p className="text-sm sm:text-base text-slate-600 font-montserrat">
            Arrastra tu archivo o haz clic para seleccionar
          </p>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-gradient-to-r from-brand-primary/10 to-[#E6B43C]/10 rounded-xl p-4 border border-brand-primary/20 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-brand-primary font-montserrat mb-2">
              Columnas requeridas:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700 font-montserrat">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-brand-green" />
                <span>Nombre y Apellido</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-brand-green" />
                <span>WhatsApp (9 dígitos)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-brand-green" />
                <span>Distrito</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-brand-green" />
                <span>Dirección</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-brand-green" />
                <span>SKU 1</span>
              </div>
            </div>
            <p className="text-xs text-slate-600 font-montserrat mt-3">
              <strong>Opcionales:</strong> Servicio, Add-ons, SKU 2-5, Cantidad 1-5, Cobrar, Notas, Latitud, Longitud
            </p>
          </div>
        </div>
      </div>

      {/* Dropzone */}
      {!selectedFile ? (
        <div
          {...getRootProps()}
          className={`
            relative border-2 border-dashed rounded-2xl p-8 sm:p-12 
            text-center cursor-pointer transition-all duration-200 mb-4
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${isDragActive 
              ? 'border-brand-primary bg-brand-primary/5 scale-[1.02]' 
              : 'border-slate-300 hover:border-brand-primary hover:bg-slate-50'
            }
          `}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-primary to-[#E6B43C] flex items-center justify-center">
              <Upload className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-base sm:text-lg font-semibold text-slate-900 font-montserrat mb-1">
                {isDragActive 
                  ? '¡Suelta el archivo aquí!' 
                  : 'Arrastra un archivo o haz clic para seleccionar'
                }
              </p>
              <p className="text-sm text-slate-600 font-montserrat">
                Archivos Excel (.xlsx, .xls)
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* File Preview */
        <div className="bg-gradient-to-r from-brand-green/10 to-brand-green/5 rounded-xl border-2 border-brand-green/30 p-4 mb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-brand-green" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 truncate font-montserrat">
                  {selectedFile.name}
                </p>
                <p className="text-xs text-slate-600 font-montserrat">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </p>
              </div>
            </div>
            <button
              onClick={onClearFile}
              disabled={disabled}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors duration-200 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Quitar archivo"
            >
              <X className="w-5 h-5 text-red-600" />
            </button>
          </div>
        </div>
      )}

      {/* Error message */}
      {dragError && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-700 font-montserrat">
            {dragError}
          </p>
        </div>
      )}

      {/* Download template button */}
      <button
        onClick={() => downloadExcelTemplate(userProducts)}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm font-montserrat disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Download className="w-5 h-5" />
        <span>Descargar plantilla con ejemplos</span>
      </button>
    </div>
  );
}

export default FileUploader;
